/**
 * 签约前营销方案 · 工单列表（2026-10-20 用户需求）。
 *
 * 需求背景
 * --------
 * 原 `/design/presign` 是「设计项目列表」的一个分类视图（矩阵 / 卡片 / 表格四套视图混用），
 * 用户要求改成**标准表格**的营销方案工单列表，列至少包含：
 *   勾选、序号、项目编号、项目地址、销售负责人、营销方案设计师、
 *   要求完成时间（洛杉矶时间）、实际完成时间、当前版本、工单状态、操作。
 *   （列头时区标注 2026-10-29 由「中国时间」改为「洛杉矶时间」，见下方 fmtCN 注释）
 * 并保留搜索、筛选、排序、分页、批量选择；移动端切卡片模式但不得横向溢出。
 *
 * 与旧页面的关系
 * -------------
 * 旧页面（`DesignV3Projects mode="presign"`）仍在 `/design/projects` 的路由里被引用，
 * 但 `/design/presign` 现在指向本页。本页**只负责营销方案工单**，不再渲染项目矩阵，
 * 避免一个页面同时承担「项目跟踪」和「方案工单」两种口径。
 *
 * 状态口径（与后端 design_presign_orders 同源）
 * -------------------------------------------
 *   待分配 = 没有负责人
 *   待制作 = 已下达、设计师还没点「标记为已完成」
 *   已完成 = 设计师已手动标记完成（销售可拿去给客户）
 *   待改进 = 客户不满意，销售退回，填了改进需求与下一次要求完成时间
 *   已关闭 = 客户不需要了 / 长期无进展，人工关闭；不进待办与催办，可恢复
 *   已签约 = 项目进入签约状态，全部只读留痕
 *
 * 关闭 / 恢复（2026-10-22 用户需求）
 * --------------------------------
 * 「签约前营销的 12835 项目现在客户不需要了，设计师不想现在再列表需要单独给个状态」，
 * 随后补充「做成已关闭……销售端要对应的显示并且能够恢复」、
 * 「设计师可以主动点击关闭，点击是确认，并让写关闭理由或上传附件（附件可选）」。
 * 行内给「关闭」（弹窗必填理由 + 可选附件）与「恢复」（一键回关闭前状态），
 * 权限位由后端 canClose / canRestore 下发，与详情页同源。
 *
 * 列表行内直接操作（2026-10-20 第二轮，用户原话）
 * ---------------------------------------------
 * 「直接列表中点击改为已完成时提示上传产物，上传了就修改状态为已完成，给对应销售提醒；
 *   销售修改为待改进需要写对应的改进需求和下一次需要的时间，修改后列表显示的要求完成时间
 *   会改为最新设置的时间……设计师看到待改进以后又去修改，修改后提交将状态修改成已完成。」
 *
 * 因此操作列不再只有「处理」一个入口，而是按后端逐行下发的权限位给出直达按钮：
 *   · canComplete（该行负责设计师 / 经理）→「改为已完成」：弹窗要求至少选 1 个产物，
 *     提交即调 `/complete-with-files`（上传 + 完成一次完成），销售立刻收到站内提醒；
 *   · canRevise（已完成行的负责设计师 / 经理）→「补交」：在弹窗中明确选
 *     「重新提交」（生成下一版）或「增加附件」（只扩充当前版）。
 *   · canImprove（该客户销售负责人 / 经理）→「改为待改进」：弹窗必填改进需求与
 *     下一次要求完成时间，提交调 `/rework`，后端把工单 dueAt 覆盖成新时间，
 *     列表「要求完成时间」列因此立即变成销售刚填的日期。
 *     2026-10-22 补充：弹窗内还可**选填佐证附件**（客户微信截图 / 现场照片 / 竞品报价），
 *     与改进要求同一次 multipart 提交，随该轮记录进详情「改进要求记录」与版本卡片。
 * 为什么权限位由后端给：列表筛选只决定「看不看得见」，能否操作取决于角色 + 线索归属，
 * 前端拿不到完整判定（assignedSales 是显示名）；用后端位可保证与详情页 permissions 同源，
 * 不会出现「列表有按钮、点进去 403」。
 *
 * 详情用抽屉展示（2026-10-20 第二轮，用户要求「参考已签约用户详情」）
 * -----------------------------------------------------------------
 * 参照 `pages/CRMSignedCustomers.jsx` 的既有做法（同样是 2026-10-13 改的抽屉化）：
 *   ① 路由 `/design/presign/:lid` **也渲染本组件**，`:lid` 存在即在列表上叠 `CrmDetailDrawer`，
 *      详情内容复用 `DesignPresignDetail`，功能 100% 保留（不做两套详情）；
 *   ② 打开抽屉时把**当前 search 一并带进详情 URL**，关闭时原样带回 —— 本页的搜索 / 分类 /
 *      排序 / 页码 / 筛选全在 URL 里，否则「关掉抽屉」会把用户的筛选条件清空；
 *   ③ URL 驱动 ⇒ 刷新、分享、浏览器后退都落在「列表 + 抽屉」同一状态上；
 *   ④ 层级用 CrmDetailDrawer 的默认第 0 层（z-45）：高于顶栏 / 侧栏，但低于 Radix 弹窗 z-50，
 *      抽屉里再开弹窗（新建工单 / 默认负责人配置）不会被盖住。
 * 抽屉宽度、拖拽调宽、Esc 只关最上层、手机满宽等行为全部由 CrmDetailDrawer 提供，本页不重复实现。
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "../../../components/ui/sonner";
import { Button } from "../../../components/ui/button";
import { ResponsiveTable } from "../../../components/mobile/ResponsiveTable";
import { CardSkeleton, TableSkeleton, EmptyState } from "../../../components/States";
import { V3Header, Panel, Denied } from "./V3Header";
import { useV3Meta, fmtD } from "./meta";
import { useApp } from "../../../context/AppContext";
import { T } from "../../../lib/i18n";
import { Search, X, Loader2, Megaphone, ChevronLeft, ChevronRight, RotateCcw, CheckCircle2, Users, Settings2, ArrowUpDown, ListChecks, Plus, Paperclip, Upload, Undo2, FileText, Trash2, CalendarRange, Ban } from "lucide-react";
import PresignSettingsPanel from "./PresignSettingsPanel";
import DesignPresignDetail from "./DesignPresignDetail";
import { CrmDetailDrawer } from "../../../components/crm/CrmDetailDrawer";
import { DESIGN_UPLOAD_ACCEPT } from "../../../lib/uploadAccept";
/* 【2026-10-29 时区口径统一】营销方案工单原来是唯一按**中国时间**显示的模块
   （列头明写「中国时间」、fmtCN 固定 Asia/Shanghai、筛选也按上海今天）。
   用户要求「全部统一按美国时间走，显示-输入都一样，中英文模式也一样」，
   所以这里改走设计部统一的洛杉矶口径；函数名保留 fmtCN 以免牵连 7 个调用点、
   但实现与语义已经变成美国太平洋时间。 */
import { fmtDT, usToday } from "../../../lib/usDate";
/* 【2026-10-30 输入侧时区提示】本页两处「要求完成时间」的 label 已经写了「洛杉矶时间」，
   但没说明「直接填美国日历日、不用换算」；未签约的销售大多是国内同事，
   按北京日期填会差一天。用 compact 形态补齐说明（悬停看全文），不改已有 label 文案。 */
import { UsDateHint } from "./UsDateHint";

/** 工单状态样式：文字 + 颜色双通道（状态不能只靠颜色表达）。 */
export const STATUS_STYLE = {
  pending_assign: { cls: "bg-slate-100 text-slate-600 border-slate-200", dot: "bg-slate-400" },
  pending_make: { cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  completed: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  rework: { cls: "bg-orange-50 text-orange-700 border-orange-200", dot: "bg-orange-500" },
  // 已关闭（2026-10-22）：客户不需要了 / 长期无进展，设计部不用再动手。
  // 用中性灰而不是红色 —— 关闭不是出错，只是「不做了」，
  // 红色徽标会让列表看起来像一堆故障。
  closed: { cls: "bg-slate-100 text-slate-500 border-slate-300", dot: "bg-slate-400" },
  signed: { cls: "bg-violet-50 text-violet-700 border-violet-200", dot: "bg-violet-500" },
};
// 页签顺序按「谁该动手」排：先待改进（销售已驳回）→ 待制作 → 待分配 → 已完成 → 已关闭 → 已签约。
// 「已关闭」放在已完成之后、已签约之前：它也是终态，但比签约更常见，用户要能一眼找到回查入口。
const CATS = [
  ["all", "全部"], ["pending_assign", "待分配"], ["pending_make", "待制作"],
  ["completed", "已完成"], ["rework", "待改进"], ["closed", "已关闭"], ["signed", "已签约"],
];
/** 截止时间筛选档位（2026-10-21：由原生日期区间改为传统下拉）。 */
const DUE_RANGES = [
  ["", "全部截止时间"], ["today", "今天到期"], ["7d", "7 天内到期"],
  ["30d", "30 天内到期"], ["month", "本月到期"], ["overdue", "已过期"],
];
// 排序选项（2026-10-22 用户需求：默认排序改为「待改进 > 待制作 > 已完成」）。
//
// 第一项 `due` 是**默认排序**，后端已改成两段式：先按状态优先级
// （待改进 → 待制作 → 待分配 → 已完成 → 已关闭/已签约），同状态内再按要求完成时间升序。
// 所以它的文案从「要求完成时间」改成「待办优先（状态 + 截止）」，
// 否则用户选「要求完成时间」却发现顺序按状态走，会以为排序坏了。
// 其余三项仍是单列排序，不混入状态优先级。
const SORTS = [
  ["due", "待办优先（状态 + 截止）"], ["actual", "实际完成时间"],
  ["created", "创建时间"], ["version", "当前版本"],
];
const PAGE_SIZES = [20, 50, 100];

/**
 * 要求完成时间显示 —— 2026-10-29 起统一为**美国太平洋时间**（函数名沿用 fmtCN）。
 *
 * 沿革：本模块原先按需求「要求完成时间（中国时间）」固定换算 Asia/Shanghai，
 * 展示、筛选、列头文案三处都用上海口径。用户后来明确「全部统一按美国时间走」，
 * 于是这里改为洛杉矶口径，与设计部其它页面（usDate）以及后端 PROJECT_TZ 一致。
 *
 * 输出格式保持本模块原有的 `YYYY-MM-DD[ HH:mm]`（不是其它页面的 MM/DD/YYYY）：
 * 这一列是密集表格的窄列，且 `dueAt` 多为纯日期字面量，保持原样最省空间。
 *   · 纯日期 `2026-10-25`：业务日历日，按字面渲染，不做时区换算；
 *   · 带时区的时间戳：先换算到洛杉矶再取日期/钟点，中美两地看到的完全一致。
 */
export const fmtCN = (v, withTime = true) => {
  if (!v) return "—";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // fmtDT 给的是「MM/DD/YYYY HH:mm」，这里只要日期与时分，拆出来重组成本列的 YYYY-MM-DD。
  const formatted = fmtDT(s, "");
  if (!formatted) return s;
  const m = formatted.match(/^(\d{2})\/(\d{2})\/(\d{4})(?: (\d{2}):(\d{2}))?$/);
  if (!m) return formatted;
  const date = `${m[3]}-${m[1]}-${m[2]}`;
  return withTime && m[4] ? `${date} ${m[4]}:${m[5]}` : date;
};

/** 逾期判定：只对未完成且有待完成时间的工单生效（已完成 / 已签约不算逾期）。 */
const isOverdue = (o) => !!o.dueAt && ["pending_assign", "pending_make", "rework"].includes(o.status)
  // 2026-10-29：逾期基准从 UTC 今天改为美国今天，与列里显示的日期同一口径。
  && String(o.dueAt).slice(0, 10) < usToday();

export default function DesignPresign() {
  const meta = useV3Meta();
  const { api, lang } = useApp();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const zh = lang === "zh";
  // 抽屉由 URL 驱动：/design/presign 是纯列表，/design/presign/:lid 是「列表 + 详情抽屉」。
  // 两个路由都渲染本组件（见 App.js），与已签约客户详情同一套做法。
  const { lid: openLid } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  // 关键词也从 URL 派生：setParam({q}) 只改 URL，若这里用独立 state 就会「URL 变了但请求没变」
  const [sel, setSel] = useState(() => new Set());
  const [showSettings, setShowSettings] = useState(false);
  const [showNew, setShowNew] = useState(false);
  // 列表行内操作：记录被点开的那一行（null = 弹窗关闭）。
  // 用整行对象而不是 leadId：弹窗要显示地址 / 负责人 / 当前版本，避免再查一次接口。
  const [completeRow, setCompleteRow] = useState(null);
  const [improveRow, setImproveRow] = useState(null);
  // 关闭弹窗（2026-10-22）：设计师或销售点「关闭」后弹出，必填理由、附件可选。
  const [closeRow, setCloseRow] = useState(null);
  const [busy, setBusy] = useState(false);

  const q = sp.get("q") || "";

  const cat = sp.get("cat") || "all";
  const sort = sp.get("sort") || "due";
  const page = Math.max(1, Number(sp.get("page") || 1));
  const pageSize = PAGE_SIZES.includes(Number(sp.get("size"))) ? Number(sp.get("size")) : 20;
  const designer = sp.get("designer") || "";
  const salesName = sp.get("salesName") || "";
  // 截止时间筛选（2026-10-21 用户要求「做成传统的下拉」）。
  // 原来是一对 <input type="date">，在 1440 以下宽度会把工具条撑得很长，
  // 而且空值显示「年/月/日」看着像没加载完。现在收敛成一个预设下拉，
  // URL 里只存 `range` 这个语义值（today / 7d / 30d / month），
  // 具体日期区间在下面换算成 dueFrom / dueTo 传给后端，后端过滤逻辑不变。
  const dueRange = sp.get("range") || "";
  const overdueOnly = sp.get("overdue") === "1";

  /**
   * 把下拉的语义值换算成后端认识的 dueFrom / dueTo（YYYY-MM-DD）。
   *
   * 为什么在前端算而不是传 range 给后端：后端 `dueFrom/dueTo` 已被其它调用方
   * （详情页筛选、外部脚本）使用，改成 range 会破坏既有口径；这里只做一次换算，
   * 接口契约保持不变。所有日期都按**美国太平洋时间**取「今天」，与列表里
   * 「要求完成时间（洛杉矶时间）」列的展示口径一致。
   * 2026-10-29 口径统一：原来这里按上海今天算，改后与设计部其它页面同源。
   */
  const dueBounds = useMemo(() => {
    if (!dueRange) return { dueFrom: "", dueTo: "" };
    // 美国今天的 YYYY-MM-DD（不再受浏览器本地时区影响）
    const todayCN = usToday();
    const shift = (days) => {
      const d = new Date(`${todayCN}T00:00:00Z`);
      d.setUTCDate(d.getUTCDate() + days);
      return d.toISOString().slice(0, 10);
    };
    if (dueRange === "today") return { dueFrom: todayCN, dueTo: todayCN };           // 今天到期
    if (dueRange === "overdue") return { dueFrom: "", dueTo: shift(-1) };            // 已过期（截止日 < 今天）
    if (dueRange === "7d") return { dueFrom: todayCN, dueTo: shift(7) };             // 7 天内到期
    if (dueRange === "30d") return { dueFrom: todayCN, dueTo: shift(30) };           // 30 天内到期
    if (dueRange === "month") {
      // 本月：当月 1 日 ~ 月末
      const first = `${todayCN.slice(0, 7)}-01`;
      const d = new Date(`${todayCN.slice(0, 7)}-01T00:00:00Z`);
      d.setUTCMonth(d.getUTCMonth() + 1);
      d.setUTCDate(0);
      return { dueFrom: first, dueTo: d.toISOString().slice(0, 10) };
    }
    return { dueFrom: "", dueTo: "" };
  }, [dueRange]);

  /** URL 是唯一筛选状态源：刷新 / 分享 / 前进后退都能复现同一视图。 */
  const setParam = useCallback((patch) => {
    const n = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") n.delete(k); else n.set(k, String(v));
    });
    // 改筛选回到第一页，避免停在越界页码上看到空列表
    if (!("page" in patch)) n.delete("page");
    setSp(n, { replace: true });
  }, [sp, setSp]);

  /**
   * 打开详情抽屉：把当前筛选 search 一起带进详情 URL。
   * 为什么必须带：关掉抽屉要回到「用户刚才看的那一屏」。筛选全在 URL 里，
   * 若详情 URL 不带 search，关闭时只能用空 search 回列表 → 搜索词 / 分类 / 页码全丢。
   * replace=true 的取舍：本页没有分页式历史栈，「打开详情再关」再用浏览器后退会退到
   * 上上页而不是列表，故用 replace 让历史里只留「列表」这一条。
   */
  const openDetail = useCallback((leadId) => {
    const s = sp.toString();
    nav(`/design/presign/${leadId}${s ? `?${s}` : ""}`);
  }, [nav, sp]);

  /** 关闭抽屉：回到列表并原样还原筛选（与 CRMSignedCustomers.closeSigned 同口径）。 */
  const closeDetail = useCallback(() => {
    const s = sp.toString();
    nav(`/design/presign${s ? `?${s}` : ""}`);
  }, [nav, sp]);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const { data } = await api.get("/design/presign/orders", {
        params: { status: cat, q, designer, salesName, dueFrom: dueBounds.dueFrom,
          dueTo: dueBounds.dueTo, sort, page, pageSize },
      });
      setD(data);
    } catch (e) {
      setErr(e?.response?.status || 0);
    }
  }, [api, cat, q, designer, salesName, dueBounds, sort, page, pageSize]);
  useEffect(() => { load(); }, [load]);

  // 搜索输入防抖：每敲一个字都打接口会让「输入中文」时请求风暴
  const [qDraft, setQDraft] = useState(q);
  useEffect(() => { setQDraft(q); }, [q]);
  useEffect(() => {
    const t = setTimeout(() => { if (qDraft !== q) setParam({ q: qDraft }); }, 350);
    return () => clearTimeout(t);
  }, [qDraft]); // eslint-disable-line

  // rows 用 useMemo 固定引用：否则 `d?.orders || []` 每次渲染都是新数组，
  // 下游以它为依赖的 useMemo（逾期筛选）每帧都会重算。
  const rows = useMemo(() => d?.orders || [], [d]);
  const counts = d?.counts || {};
  const total = d?.total || 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  // 逾期筛选在前端做：后端只按状态与截止区间过滤，逾期是「今天 vs 截止日」的派生判断
  const shown = useMemo(() => (overdueOnly ? rows.filter(isOverdue) : rows), [rows, overdueOnly]);
  const allSelected = shown.length > 0 && shown.every((r) => sel.has(r.leadId));
  const toggleAll = () => {
    const n = new Set(sel);
    if (allSelected) shown.forEach((r) => n.delete(r.leadId));
    else shown.forEach((r) => n.add(r.leadId));
    setSel(n);
  };
  const toggle = (id) => {
    const n = new Set(sel);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSel(n);
  };
  const clearSel = () => setSel(new Set());

  /** 批量改派：仅经理可用（后端同样校验），跳过已签约。 */
  const [bulkTo, setBulkTo] = useState("");
  const [designers, setDesigners] = useState([]);
  useEffect(() => {
    api.get("/design/presign/designers").then(({ data }) => setDesigners(data.designers || [])).catch(() => {});
  }, [api]);

  /**
   * 恢复已关闭工单（2026-10-22 用户需求：「销售端……能够恢复」）。
   *
   * 为什么不弹二次确认：恢复本身是**可逆且安全**的方向 —— 最坏情况是又点一次「关闭」，
   * 两次操作都会写 history 留痕。反而每次恢复都弹框会让「客户又回来了」这种
   * 需要尽快恢复制作的场景变慢。关闭方向才需要弹框（必填理由），恢复不需要。
   *
   * 恢复目标状态由后端 `closedFromStatus` 决定（关闭前是待制作就回待制作，
   * 是已完成就回已完成），前端不猜、也不允许传状态，避免前端传错把单恢复成错误状态。
   */
  const restoreRow = async (row) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/design/presign/orders/${row.leadId}/restore`);
      toast.success(T("已恢复为「{0}」", [data.order?.statusLabel || T("待制作")]));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  const bulkReassign = async () => {
    if (!bulkTo) { toast.error(T("请选择新的营销方案设计师")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/design/presign/orders/bulk-reassign", {
        leadIds: Array.from(sel), designer: bulkTo,
      });
      toast.success(T("已改派 {0} 个工单{1}", [data.updated?.length || 0,
        data.skipped?.length ? T("，跳过 {0} 个（已签约 / 无工单）", [data.skipped.length]) : ""]));
      clearSel(); setBulkTo(""); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  if (!meta) return <div className="space-y-4" data-testid="dv3-presign-loading"><CardSkeleton count={3} /><div className="rounded-lg bg-white border border-slate-200"><TableSkeleton rows={6} cols={6} /></div></div>;
  if (meta.error) return <Denied status={meta?.status} />;

  const activeFilters = [designer, salesName, dueRange, overdueOnly].filter(Boolean).length;

  return (
    <div className="space-y-3" data-testid="dv3-presign-orders" data-mode="orders">
      <V3Header title={T("签约前营销方案")} right={
        <div className="flex flex-wrap items-center gap-2">
          {/* 新建工单：销售从本人负责的 CRM 客户里挑一个，进详情填要求完成时间后下达。
              为什么不在列表弹全表单：下达需要「要求完成时间 + 负责人联系方式确认」，
              详情页操作区已经完整承担这件事，列表弹窗只做客户选择，避免两套表单漂移。 */}
          {d?.canCreate && (
            <Button size="sm" data-testid="dv3-presign-new-btn" onClick={() => setShowNew(true)}>
              <Plus className="h-4 w-4" />{T("新建工单")}
            </Button>
          )}
          {meta.isManager && (
            <Button size="sm" variant="outline" data-testid="dv3-presign-settings-btn" onClick={() => setShowSettings(true)}>
              <Settings2 className="h-4 w-4" />{T("默认负责人")}
            </Button>
          )}
          <Button size="sm" variant="outline" data-testid="dv3-presign-refresh" onClick={load}>
            <RotateCcw className="h-4 w-4" />{T("刷新")}
          </Button>
        </div>
      } />

      {/* 工具条：状态分类 + 搜索 + 排序 + 筛选。移动端折叠，桌面常显。 */}
      <section className="min-w-0 rounded-lg border border-slate-200 bg-white p-2 space-y-2" data-testid="dv3-presign-filters">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex shrink-0 items-center rounded-lg bg-slate-100 p-0.5 max-sm:w-full overflow-x-auto" role="tablist" aria-label={T("工单状态分类")} data-testid="dv3-presign-cats">
            {CATS.map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={cat === k}
                data-testid={`dv3-presign-cat-${k}`} data-count={counts[k] || 0}
                onClick={() => setParam({ cat: k === "all" ? "" : k })}
                className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 px-2.5 py-1 text-[11px] font-semibold rounded-md whitespace-nowrap ${cat === k ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
                {T(label)} <span className="text-slate-400 font-normal">{counts[k] || 0}</span>
              </button>
            ))}
          </div>
          <span className="sr-only" data-testid="dv3-presign-cat-current" data-value={cat} />
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input value={qDraft} onChange={(e) => setQDraft(e.target.value)} data-testid="dv3-presign-search"
              placeholder={T("搜索地址 / 客户 / 项目编号 / 负责人")}
              className="w-full min-h-11 md:min-h-0 rounded-lg border border-slate-200 pl-8 pr-8 py-1.5 text-xs" />
            {qDraft && <button type="button" aria-label={T("清除搜索")} onClick={() => { setQDraft(""); setParam({ q: "" }); }}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"><X className="h-3.5 w-3.5" /></button>}
          </div>
          <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
            <ArrowUpDown className="h-3.5 w-3.5" />
            <select value={sort} onChange={(e) => setParam({ sort: e.target.value })} data-testid="dv3-presign-sort"
              className="min-h-11 md:min-h-0 rounded-md border border-slate-200 px-2 py-1 text-[11px] bg-white">
              {SORTS.map(([k, l]) => <option key={k} value={k}>{T(l)}</option>)}
            </select>
          </label>
          <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
            <Users className="h-3.5 w-3.5" />
            <select value={designer} onChange={(e) => setParam({ designer: e.target.value })} data-testid="dv3-presign-filter-designer"
              className="min-h-11 md:min-h-0 rounded-md border border-slate-200 px-2 py-1 text-[11px] bg-white max-w-[160px]">
              <option value="">{T("全部设计师")}</option>
              {designers.map((x) => <option key={x.username} value={x.username}>{x.name}</option>)}
            </select>
          </label>
          {/* 截止时间：传统下拉（2026-10-21 用户要求）。
              原来是一对原生日期输入，占宽且空值显示「年/月/日」容易被当成没加载完。
              现在只给「今天到期 / 7 天内 / 30 天内 / 本月 / 已过期」这几个真正会用到的档位，
              具体区间由 dueBounds 换算成接口的 dueFrom/dueTo。 */}
          <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
            <CalendarRange className="h-3.5 w-3.5" />
            <select value={dueRange} onChange={(e) => setParam({ range: e.target.value })} data-testid="dv3-presign-due-range"
              className="min-h-11 md:min-h-0 rounded-md border border-slate-200 px-2 py-1 text-[11px] bg-white">
              {DUE_RANGES.map(([k, l]) => <option key={k} value={k}>{T(l)}</option>)}
            </select>
          </label>
          <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 select-none">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="dv3-presign-check-all"
              aria-label={T("全选本页")} className="h-4 w-4" disabled={!shown.length} />
            {T("全选本页")}
          </label>
          <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 select-none">
            <input type="checkbox" checked={overdueOnly} onChange={(e) => setParam({ overdue: e.target.checked ? "1" : "" })} data-testid="dv3-presign-overdue-only" className="h-4 w-4" />
            {T("仅看逾期")}
          </label>
          {activeFilters > 0 && (
            <Button size="sm" variant="ghost" data-testid="dv3-presign-clear-filters"
              onClick={() => setParam({ designer: "", salesName: "", range: "", overdue: "" })}>
              <X className="h-3.5 w-3.5" />{T("清除筛选")}
            </Button>
          )}
        </div>

        {/* 批量操作条：勾选后出现，明确显示已选数量与取消入口（Skill 要求）。 */}
        {sel.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-violet-200 bg-violet-50/60 px-2.5 py-2" data-testid="dv3-presign-bulkbar">
            <span className="text-xs font-semibold text-violet-800" data-testid="dv3-presign-selcount">{T("已选 {0} 项", [sel.size])}</span>
            {meta.isManager && (
              <>
                <select value={bulkTo} onChange={(e) => setBulkTo(e.target.value)} data-testid="dv3-presign-bulk-designer"
                  className="min-h-11 md:min-h-0 rounded-md border border-violet-200 px-2 py-1 text-[11px] bg-white">
                  <option value="">{T("选择新负责人")}</option>
                  {designers.map((x) => <option key={x.username} value={x.username}>{x.name}</option>)}
                </select>
                <Button size="sm" data-testid="dv3-presign-bulk-apply" disabled={busy || !bulkTo} onClick={bulkReassign}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}{T("批量改派")}
                </Button>
              </>
            )}
            <Button size="sm" variant="ghost" data-testid="dv3-presign-bulk-clear" onClick={clearSel}>{T("取消选择")}</Button>
          </div>
        )}
      </section>

      {/* 主表：标准表格（需求第一条：默认标准表格，不要矩阵或卡片）。
          ResponsiveTable 在 <768px 自动转成字段卡片，避免横向溢出。 */}
      <Panel tid="dv3-presign-table-panel" className="min-w-0" title={T("营销方案工单")} icon={Megaphone}
        extra={<span className="text-[11px] text-slate-500" data-testid="dv3-presign-total">{T("共 {0} 条", [total])}</span>}>
        {err ? (
          <div className="py-8 text-center" data-testid="dv3-presign-error">
            <p className="text-sm text-red-600">{T("加载失败，请稍后重试")}</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={load}>{T("重试")}</Button>
          </div>
        ) : !d ? (
          <TableSkeleton rows={8} cols={6} />
        ) : shown.length === 0 ? (
          <EmptyState title={T("暂无营销方案工单")}
            hint={cat !== "all" || q || activeFilters ? T("当前筛选条件下没有工单，试试切换分类或清除筛选。") : T("销售在 CRM 客户详情下达营销方案要求后，工单会出现在这里。")} />
        ) : (
          <ResponsiveTable mobile="cards" className="w-full text-xs" data-testid="dv3-presign-table"
            mobileLabels={[T("选择"), T("序号"), T("项目编号"), T("项目地址"), T("销售负责人"), T("营销方案设计师"),
              T("要求完成时间"), T("实际完成时间"), T("当前版本"), T("工单状态"), T("操作")]}>
            <thead>
              <tr className="text-left text-[11px] text-slate-500 border-b border-slate-200">
                {/* 表头不能放 checkbox：ResponsiveTable 见到表头控件会整表降级成横向滚动，
                    手机端就失去卡片模式。全选改放到上方工具条（见 dv3-presign-check-all），
                    这里只留一个同宽的空列占位。 */}
                <th className="w-8 py-1.5"><span className="sr-only">{T("选择")}</span></th>
                <th className="w-10 py-1.5">{T("序号")}</th>
                <th className="py-1.5 pr-4">{T("项目编号")}</th>
                <th className="py-1.5 pr-4">{T("项目地址")}</th>
                <th className="py-1.5 pr-4 whitespace-nowrap">{T("销售负责人")}</th>
                <th className="py-1.5 pr-4 whitespace-nowrap">{T("营销方案设计师")}</th>
                {/* 时区口径不能省（时区统一后仍要让人一眼看出这是哪个时区），
                    但整串放一行会把操作列挤窄，所以主行写「要求完成时间」，
                    第二行小字标注「洛杉矶时间」。2026-10-29 前这里标的是「中国时间」。 */}
                <th className="py-1.5 pr-4 whitespace-nowrap">
                  {T("要求完成时间")}
                  <span className="block text-[9px] font-normal leading-3 text-slate-400" data-testid="dv3-presign-due-tz">{T("洛杉矶时间")}</span>
                </th>
                <th className="py-1.5 pr-4 whitespace-nowrap">{T("实际完成时间")}</th>
                {/* 「当前版本」与「工单状态」之间留出固定间距（2026-10-21 用户反馈这两列太近）：
                    版本列右对齐 + pr-6，状态列 pl-2，两列内容至少隔开 32px。 */}
                <th className="w-[64px] py-1.5 pr-6 text-right whitespace-nowrap">{T("当前版本")}</th>
                <th className="py-1.5 pl-2 whitespace-nowrap">{T("工单状态")}</th>
                <th className="py-1.5 pl-6 text-right whitespace-nowrap">{T("操作")}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r, i) => {
                const st = STATUS_STYLE[r.status] || STATUS_STYLE.pending_assign;
                const checked = sel.has(r.leadId);
                const over = isOverdue(r);
                return (
                  <tr key={r.leadId} data-testid={`dv3-presign-row-${r.leadId}`} data-status={r.status}
                    data-lead={r.leadId}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70">
                    <td className="py-1.5">
                      <input type="checkbox" checked={checked} onChange={() => toggle(r.leadId)}
                        data-testid={`dv3-presign-check-${r.leadId}`} aria-label={T("选择 {0}", [r.address || r.leadId])}
                        className="h-4 w-4" />
                    </td>
                    <td className="py-1.5 text-slate-400 tabular-nums">{(page - 1) * pageSize + i + 1}</td>
                    <td className="py-1.5 pr-4 font-mono text-[11px] text-slate-600 whitespace-nowrap">{r.projectId || "—"}</td>
                    <td className="py-1.5 pr-4 min-w-[150px] max-w-[320px]">
                      <button type="button" onClick={() => openDetail(r.leadId)}
                        data-testid={`dv3-presign-open-${r.leadId}`}
                        className="text-left text-slate-800 hover:text-violet-700 hover:underline font-medium">
                        <span className="line-clamp-2">{r.address || "—"}</span>
                      </button>
                      {r.clientName && <span className="block text-[10px] text-slate-400 truncate">{r.clientName}{r.city ? ` · ${r.city}` : ""}</span>}
                    </td>
                    <td className="py-1.5 pr-4 text-slate-600 whitespace-nowrap">{r.salesName || "—"}</td>
                    <td className="py-1.5 pr-4 text-slate-600 whitespace-nowrap">
                      {r.designerName || r.designer || <span className="text-slate-400">{T("未分配")}</span>}
                    </td>
                    <td className="py-1.5 pr-4 whitespace-nowrap tabular-nums">
                      <span className={over ? "text-red-600 font-semibold" : "text-slate-600"}>{fmtCN(r.dueAt, false)}</span>
                      {over && <span className="ml-1 text-[10px] text-red-500">{T("逾期")}</span>}
                    </td>
                    <td className="py-1.5 pr-4 whitespace-nowrap tabular-nums text-slate-600">{fmtCN(r.actualFinishAt)}</td>
                    <td className="py-1.5 pr-6 text-right tabular-nums text-slate-700">
                      {r.version ? `v${r.version}` : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-1.5 pl-2">
                      <span data-testid={`dv3-presign-status-${r.leadId}`} data-status={r.status}
                        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-semibold ${st.cls}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />{T(r.statusLabel)}
                      </span>
                    </td>
                    <td className="py-1.5 pl-6 text-right whitespace-nowrap">
                      <div className="inline-flex flex-nowrap items-center justify-end gap-1">
                        {/* 设计师：改为已完成（先弹窗选产物，上传即完成）
                            为什么放在行内：设计师一天要过十几单，进详情再回列表会丢筛选位置；
                            弹窗里必须选文件，对应需求「点击改为已完成时提示上传产物」。
                            按钮文案按角色简写（2026-10-21 用户要求）：设计师看到「已完成」，
                            销售看到「待改进」—— 行内按钮空间紧，长文案会挤掉相邻列。 */}
                        {r.canComplete && (
                          <button type="button" onClick={() => setCompleteRow(r)}
                            data-testid={`dv3-presign-complete-${r.leadId}`}
                            className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-emerald-200 bg-emerald-50 px-1.5 py-1 text-[11px] text-emerald-700 hover:bg-emerald-100">
                            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />{T("已完成")}
                          </button>
                        )}
                        {/* 已完成后由设计师主动选补交方式，避免把同版补附件误当成 v+1。
                            列表保留一个紧凑入口，弹窗内并列展示两种清晰的选项。 */}
                        {r.canRevise && (
                          <button type="button" onClick={() => setCompleteRow(r)}
                            data-testid={`dv3-presign-revise-${r.leadId}`}
                            className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-violet-200 bg-violet-50 px-1.5 py-1 text-[11px] text-violet-700 hover:bg-violet-100 focus-visible:ring-2 focus-visible:ring-violet-400">
                            <Upload className="h-3.5 w-3.5 shrink-0" />{T("补交")}
                          </button>
                        )}
                        {/* 销售：改为待改进（必填改进需求 + 下一次要求完成时间）
                            已处于待改进时按钮仍保留：客户又提新意见时可以改新一轮要求。 */}
                        {r.canImprove && (
                          <button type="button" onClick={() => setImproveRow(r)}
                            data-testid={`dv3-presign-improve-${r.leadId}`}
                            className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-orange-200 bg-orange-50 px-1.5 py-1 text-[11px] text-orange-700 hover:bg-orange-100">
                            <Undo2 className="h-3.5 w-3.5 shrink-0" />{T("待改进")}
                          </button>
                        )}
                        {/* 关闭 / 恢复（2026-10-22 用户需求）：
                            「设计师可以主动点击关闭，点击是确认，并让写关闭理由或上传附件（附件可选）」、
                            「销售端要对应的显示并且能够恢复」。
                            · 关闭入口对「负责设计师 + 销售负责人 + 经理」开放（后端 canClose 同源）；
                            · 已关闭的行换成「恢复」，不再给制作 / 改进按钮（后端同样拒绝）；
                            · 按钮用中性灰：关闭是常规收尾动作，不是危险操作，红色会让人不敢点。 */}
                        {r.canClose && (
                          <button type="button" onClick={() => setCloseRow(r)}
                            data-testid={`dv3-presign-close-${r.leadId}`}
                            className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-slate-300 bg-white px-1.5 py-1 text-[11px] text-slate-600 hover:bg-slate-50">
                            <Ban className="h-3.5 w-3.5 shrink-0" />{T("关闭")}
                          </button>
                        )}
                        {r.canRestore && (
                          <button type="button" onClick={() => restoreRow(r)}
                            data-testid={`dv3-presign-restore-${r.leadId}`}
                            className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-violet-200 bg-violet-50 px-1.5 py-1 text-[11px] text-violet-700 hover:bg-violet-100">
                            <RotateCcw className="h-3.5 w-3.5 shrink-0" />{T("恢复")}
                          </button>
                        )}
                        {/* 详情入口：所有人统一显示「详情」（2026-10-21 用户要求）。
                            之前按 readOnly 显示「查看 / 处理」，同一列出现两种说法，
                            用户无法预判点进去能做什么；详情本身就是「看 + 可操作」的同一页。 */}
                        <button type="button" onClick={() => openDetail(r.leadId)}
                          data-testid={`dv3-presign-detail-${r.leadId}`}
                          className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-slate-200 px-1.5 py-1 text-[11px] text-slate-600 hover:border-violet-300 hover:text-violet-700">
                          <ListChecks className="h-3.5 w-3.5 shrink-0" />{T("详情")}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </ResponsiveTable>
        )}

        {/* 分页：桌面紧凑、移动端按钮仍满足 44px 触控。 */}
        {total > 0 && (
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2" data-testid="dv3-presign-pager">
            <span className="text-[11px] text-slate-500">{T("第 {0} / {1} 页 · 共 {2} 条", [page, pages, total])}</span>
            <div className="flex items-center gap-1">
              <select value={pageSize} onChange={(e) => setParam({ size: e.target.value, page: 1 })} data-testid="dv3-presign-pagesize"
                className="min-h-11 md:min-h-0 rounded-md border border-slate-200 px-1.5 py-1 text-[11px] bg-white">
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{T("{0} 条/页", [n])}</option>)}
              </select>
              <Button size="sm" variant="outline" data-testid="dv3-presign-prev" disabled={page <= 1}
                onClick={() => setParam({ page: page - 1 })}><ChevronLeft className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" data-testid="dv3-presign-next" disabled={page >= pages}
                onClick={() => setParam({ page: page + 1 })}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </Panel>

      {showSettings && <PresignSettingsPanel api={api} onClose={() => setShowSettings(false)} designers={designers} />}
      {showNew && <NewOrderDialog api={api} onOpen={openDetail} onClose={() => setShowNew(false)} />}

      {/* 行内操作弹窗（2026-10-20 第二轮）。
          z-[95]：高于 CrmDetailDrawer（z-45）与 Radix 弹窗（z-50），低于全局最高层；
          与同页「新建工单」「默认负责人」两个手写弹窗保持同一层级口径。 */}
      {completeRow && (
        <CompleteWithFilesDialog api={api} row={completeRow} mode={completeRow.canRevise ? "choose" : "complete"}
          onClose={() => setCompleteRow(null)}
          onDone={() => { setCompleteRow(null); load(); }} />
      )}
      {improveRow && (
        <ImproveDialog api={api} row={improveRow}
          onClose={() => setImproveRow(null)}
          onDone={() => { setImproveRow(null); load(); }} />
      )}
      {closeRow && (
        <CloseOrderDialog api={api} row={closeRow}
          onClose={() => setCloseRow(null)}
          onDone={() => {
            setCloseRow(null);
            // 关闭成功后清掉搜索词（2026-10-22 用户反馈：「我关闭一个后列表只显示这个关闭的了」）。
            // 原因不是列表坏了：用户是搜着这单去关的，关闭只改状态、不改搜索条件，
            // 于是整个列表被搜索词过滤成只剩这一条「已关闭」的单，页签数字也跟着变成
            // 已完成 0 / 待制作 0，看起来像其它工单全丢了。清掉 q 回到完整列表，
            // 用户能立刻看到其它单都还在，配合 toast 指向「已关闭」页签。
            // 只清 q、不动 cat：本来在「待制作」页签时，关闭后该行消失正是预期效果。
            if (q) setParam({ q: "" }); else load();
          }} />
      )}

      {/* 详情抽屉：由 URL 的 :lid 驱动（/design/presign/:lid 也渲染本组件）。
          内容复用 DesignPresignDetail，功能 100% 保留；embedded 让它不再渲染页面级返回按钮，
          onClose 负责把 URL 切回带筛选的列表。层级交给 CrmDetailDrawer 默认第 0 层（z-45），
          低于 Radix 弹窗 z-50，抽屉里再开「新建工单 / 默认负责人」弹窗不会被盖住。 */}
      {openLid && (
        <CrmDetailDrawer title={T("营销方案工单详情")} onClose={closeDetail} testId="dv3-presign-drawer">
          <DesignPresignDetail embedded onClose={closeDetail} onChanged={load} />
        </CrmDetailDrawer>
      )}
    </div>
  );
}

/* 新建工单 · 选择客户（2026-10-20 用户需求）
   需求：「销售创建任务」要能从模块内发起，不必绕回 CRM 客户详情。
   这里只选客户，选中后跳到详情页 —— 要求完成时间、负责人确认都在详情操作区完成，
   保证「没填要求完成时间不能发送」这一条只有一个入口、一套校验。 */
const NewOrderDialog = ({ api, onOpen, onClose }) => {
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      setBusy(true);
      api.get("/design/presign/candidates", { params: { q } })
        .then(({ data }) => setD(data)).catch(() => setD({ candidates: [] }))
        .finally(() => setBusy(false));
    }, 250);
    return () => clearTimeout(t);
  }, [api, q]);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  const list = d?.candidates || [];
  return (
    <div className="fixed inset-0 z-[95] bg-slate-900/40 flex items-start justify-center p-3 sm:p-6 overflow-y-auto" data-testid="dv3-presign-new">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl border border-slate-200">
        <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
          <p className="text-sm font-semibold text-slate-800">{T("新建营销方案工单")}</p>
          <button onClick={onClose} aria-label={T("关闭")} data-testid="dv3-presign-new-close"
            className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 p-1 text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="dv3-presign-new-search"
              placeholder={T("搜索客户姓名 / 地址 / 电话")}
              className="w-full min-h-11 md:min-h-0 rounded-lg border border-slate-200 pl-8 pr-3 py-1.5 text-xs" />
          </div>
          <p className="text-[11px] text-slate-500">{T("选择客户后进入详情填写要求完成时间并下达；已签约客户不会再出现在这里。")}</p>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100" data-testid="dv3-presign-new-list">
            {busy && !list.length && <p className="py-6 text-center text-xs text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-1" />{T("加载中…")}</p>}
            {!busy && !list.length && <p className="py-6 text-center text-xs text-slate-400">{T("没有可下达的客户")}</p>}
            {list.map((c) => (
              <button key={c.leadId} data-testid={`dv3-presign-new-item-${c.leadId}`}
                onClick={() => { onClose(); onOpen(c.leadId); }}
                className="w-full text-left px-1 py-2.5 hover:bg-violet-50/60 rounded-md">
                <p className="text-xs font-semibold text-slate-800 truncate">{c.clientName || c.address || c.leadId}</p>
                <p className="text-[11px] text-slate-500 truncate">{c.address}{c.phone ? ` · ${c.phone}` : ""}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* 列表行内操作 · 通用弹窗外壳
   两个行内弹窗（已完成 / 待改进）结构一致：标题栏 + 说明 + 表单 + 底部按钮。
   抽一个壳是为了保证键盘可达性、Esc 关闭、点遮罩关闭、层级（z-[95]）三处完全一致 ——
   之前项目里出现过「某个弹窗 Esc 不关」「点遮罩把已填内容一起关掉」的不一致问题。
   忙碌中不响应 Esc / 遮罩点击：提交到一半被关掉会让用户以为没生效。 */
const RowDialogShell = ({ tid, title, subtitle, icon: Icon, iconCls = "text-violet-500",
                          onClose, busy, footer, children }) => {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, busy]);
  return (
    <div className="fixed inset-0 z-[95] flex items-start justify-center overflow-y-auto bg-slate-900/40 p-3 sm:items-center sm:p-6"
      data-testid={tid} onClick={() => !busy && onClose()}>
      <div role="dialog" aria-modal="true" aria-label={title}
        className="w-full max-w-lg rounded-xl border border-slate-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 border-b border-slate-100 px-4 py-3">
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
              <Icon className={`h-4 w-4 ${iconCls}`} />{title}
            </p>
            {subtitle && <p className="mt-0.5 truncate text-[11px] text-slate-500" title={subtitle}>{subtitle}</p>}
          </div>
          <button type="button" onClick={() => !busy && onClose()} aria-label={T("关闭")}
            data-testid={`${tid}-close`}
            className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 p-1 text-slate-400 hover:text-slate-700">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 p-4">{children}</div>
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 px-4 py-3">{footer}</div>
      </div>
    </div>
  );
};

/* 列表行内操作 · 改为已完成（上传产物一步到位）
   需求原话（2026-10-20 第二轮）：
     「直接列表中点击改为已完成时提示上传产物...上传了就修改状态为已完成..给对应销售提醒」
   设计取舍
   --------
   · 弹窗一打开就提示「选择产物」，且**未选文件时提交按钮禁用** —— 后端同样校验，
     前端先挡住是为了不让人跑一次 400；
   · 提交走 `/complete-with-files`（multipart，一次请求完成上传 + 写版本 + 通知），
     不复用详情页的「先传草稿再点完成」两步，避免销售拿到「只有草稿、没有版本」的工单；
   · 提交成功后 onDone 会让列表重新拉取：状态列变「已完成」、实际完成时间出现、版本号 +1，
     都是后端返回的真实值，前端不做乐观更新（乐观更新会掩盖后端失败）。 */
/**
 * 方案附件弹窗。待制作/待改进沿用旧的「提交即完成」入口；已完成时先让设计师
 * 明确选「重新提交」或「增加附件」，分别生成下一版或扩充当前版。
 * 两个已完成动作共用文件选择和错误反馈，防止列表与详情走出不同数据口径。
 */
export const CompleteWithFilesDialog = ({ api, row, mode = "complete", onClose, onDone }) => {
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [choice, setChoice] = useState(mode === "choose" ? "" : mode);
  const inputRef = useRef(null);
  const currentVersion = Number(row.version) || 1;
  const initial = mode === "complete";
  const tid = initial ? "dv3-presign-complete" : "dv3-presign-revise";
  const submitLabel = choice === "append" ? T("增加到 v{0}", [currentVersion])
    : choice === "resubmit" ? T("重新提交为 v{0}", [currentVersion + 1])
      : T(initial ? "上传并标记完成" : "请先选择方式");
  const chooseMode = (next) => {
    if (next === choice) return;
    // 切换补交语义时清空已选文件，避免本想补附件的文件误生成新版本。
    setChoice(next); setFiles([]); setNote("");
  };

  const add = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) setFiles((old) => [...old, ...picked]);
    if (inputRef.current) inputRef.current.value = "";
  };
  const drop = (i) => setFiles((old) => old.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!files.length) { toast.error(T("请至少选择 1 个方案产物")); return; }
    if (!choice) { toast.error(T("请选择重新提交或增加附件")); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (choice !== "append") fd.append("note", note.trim());
      if (choice === "resubmit") fd.append("resubmit", "true");
      const endpoint = choice === "append" ? "append-files" : "complete-with-files";
      const { data } = await api.post(`/design/presign/orders/${row.leadId}/${endpoint}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      if (choice === "append") {
        toast.success(T("已为 v{0} 增加 {1} 个附件，销售可查看全部 {2} 个附件", [data.version, data.uploaded, data.fileCount]));
      } else if (choice === "resubmit") {
        toast.success(T("已重新提交 v{0}，销售已收到提醒", [data.version]));
      } else {
        toast.success(T("已上传 {0} 个产物并标记完成，已生成 v{1}，已提醒销售", [data.uploaded || files.length, data.version]));
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  return (
    <RowDialogShell tid={`${tid}-dialog`} title={T(initial ? "改为已完成" : "补交方案")}
      subtitle={`${row.address || ""}${row.projectId ? ` · ${row.projectId}` : ""}`}
      icon={initial ? CheckCircle2 : Upload} iconCls={initial ? "text-emerald-600" : "text-violet-600"} onClose={onClose} busy={busy}
      footer={<>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onClose} data-testid={`${tid}-cancel`}>{T("取消")}</Button>
        <Button size="sm" className={initial ? "bg-emerald-600 hover:bg-emerald-700" : ""} disabled={busy || !files.length || !choice}
          onClick={submit} data-testid={`${tid}-submit`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : choice === "append" ? <Paperclip className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          {submitLabel}
        </Button>
      </>}>
      {initial ? (
        <p className="rounded-md bg-emerald-50 px-2.5 py-2 text-[11px] leading-5 text-emerald-800">
          {T("上传方案产物即视为本版交付：提交后立即生成新版本、工单状态变为「已完成」，并提醒销售负责人拿给客户确认。")}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="dv3-presign-revise-choices">
          <button type="button" disabled={busy} onClick={() => chooseMode("resubmit")}
            aria-pressed={choice === "resubmit"} data-testid="dv3-presign-choice-resubmit"
            className={`rounded-lg border p-2 text-left text-xs focus-visible:ring-2 focus-visible:ring-violet-400 ${choice === "resubmit" ? "border-violet-400 bg-violet-50 text-violet-800" : "border-slate-200 bg-white text-slate-700 hover:border-violet-300"}`}>
            <span className="block font-semibold">{T("重新提交")} · v{currentVersion + 1}</span>
            <span className="mt-1 block text-[11px] text-slate-500">{T("新产物作为下一版，旧版完整保留")}</span>
          </button>
          <button type="button" disabled={busy} onClick={() => chooseMode("append")}
            aria-pressed={choice === "append"} data-testid="dv3-presign-choice-append"
            className={`rounded-lg border p-2 text-left text-xs focus-visible:ring-2 focus-visible:ring-violet-400 ${choice === "append" ? "border-violet-400 bg-violet-50 text-violet-800" : "border-slate-200 bg-white text-slate-700 hover:border-violet-300"}`}>
            <span className="block font-semibold">{T("增加附件")} · v{currentVersion}</span>
            <span className="mt-1 block text-[11px] text-slate-500">{T("保留当前版及原附件，销售可查看全部")}</span>
          </button>
        </div>
      )}

      {(initial || choice) && <div>
        {choice === "resubmit" && Number(row.draftCount) > 0 && (
          <p className="mb-2 text-[11px] text-slate-600" data-testid="dv3-presign-revise-drafts-hint">
            {T("已有 {0} 个未完成草稿也会并入新版本。", [row.draftCount])}
          </p>
        )}
        <p className="mb-1 text-[11px] font-semibold text-slate-600">
          {T("方案产物")} <span className="text-red-500">*</span>
        </p>
        <input ref={inputRef} type="file" multiple accept={DESIGN_UPLOAD_ACCEPT} className="hidden"
          onChange={add} data-testid={`${tid}-file-input`} />
        <Button size="sm" variant="outline" className="w-full" disabled={busy}
          onClick={() => inputRef.current?.click()} data-testid={`${tid}-pick`}>
          <Paperclip className="h-4 w-4" />{T("选择文件（可多选）")}
        </Button>
        <p className="mt-1 text-[10px] text-slate-400">
          {T("支持平面图、效果图、PPT、PDF、CAD、CDR 等；同一版本可包含多个附件。")}
        </p>
        {files.length > 0 && (
          <ul className="mt-2 space-y-1" data-testid={`${tid}-files`}>
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center gap-1.5 rounded-md bg-slate-50 px-2 py-1 text-[11px] text-slate-600">
                <FileText className="h-3 w-3 shrink-0 text-slate-400" />
                <span className="min-w-0 flex-1 truncate" title={f.name}>{f.name}</span>
                <span className="shrink-0 tabular-nums text-slate-400">{(f.size / 1024 / 1024).toFixed(2)} MB</span>
                <button type="button" aria-label={T("移除 {0}", [f.name])} disabled={busy}
                  onClick={() => drop(i)} data-testid={`${tid}-remove-${i}`}
                  className="p-0.5 text-slate-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
              </li>
            ))}
          </ul>
        )}
        {!files.length && (
          <p className="mt-2 text-[10px] text-slate-400" data-testid={`${tid}-empty`}>{T("还没有选择文件；至少需要 1 个附件才能提交。")}</p>
        )}
      </div>}

      {choice && choice !== "append" && <label className="block text-[11px] font-semibold text-slate-600">
        {T("提交说明（可选）")}
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} disabled={busy}
          data-testid={`${tid}-note-input`}
          placeholder={T("例如：已按客户意见调整后院露台，效果图更新到第 3 版")}
          className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
      </label>}
    </RowDialogShell>
  );
};

/* 列表行内操作 · 改为待改进（销售填写改进需求 + 下一次要求完成时间）
   需求原话（2026-10-20 第二轮）：
     「销售修改为待改进需要将写对应的改进需求，和下一次需要的时间是多久...
       修改后列表显示的要求完成时间会就改为最新设置的时间..这些记录都需要在详情中显示处理」
   实现要点
   --------
   · 两项都必填：后端 `/rework` 有同样校验（400），前端禁用按钮是第一道闸；
   · 下一次要求完成时间**不预填**：销售人员多数只是往后挪几天，但如果预填当前值，
     很容易手滑直接提交，把「旧截止时间」当成「新要求」发出去，设计师收到的提醒就自相矛盾；
   · 提交成功后列表重新拉取，后端已把 presign.dueAt 覆盖成新时间，
     所以「要求完成时间」列直接显示新值 —— 不再需要前端自己拼；
   · 记录不是只存在列表：`improveRequirement` / `improveDueAt` / 操作人 / 时间
     都写进工单与版本记录，详情页逐轮展示（见 DesignPresignDetail 的改进记录块）。 */
const ImproveDialog = ({ api, row, onClose, onDone }) => {
  const [due, setDue] = useState("");
  const [requirement, setRequirement] = useState("");
  // 佐证附件（2026-10-22 用户需求「列表和详情待改进点击时可以选上传佐证」）。
  // 与「关闭工单」同构：改进需求 / 时间必填，佐证选填，一次 multipart 请求提交 ——
  // 拆成「先传附件、再点待改进」会在用户中途放弃时留下孤儿文件。
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const add = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) setFiles((old) => [...old, ...picked]);
    if (inputRef.current) inputRef.current.value = "";
  };
  const drop = (i) => setFiles((old) => old.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!requirement.trim()) { toast.error(T("请填写改进需求")); return; }
    if (!due) { toast.error(T("请填写下一次要求完成时间")); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("dueAt", due);
      fd.append("reason", requirement.trim());
      files.forEach((f) => fd.append("files", f));
      await api.post(`/design/presign/orders/${row.leadId}/rework`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(T("已改为待改进，设计师已收到提醒；要求完成时间已更新为 {0}", [due]));
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  return (
    <RowDialogShell tid="dv3-presign-improve-dialog" title={T("改为待改进")}
      subtitle={`${row.address || ""}${row.version ? ` · ${T("当前 v{0}", [row.version])}` : ""}`}
      icon={Undo2} iconCls="text-orange-600" onClose={onClose} busy={busy}
      footer={<>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onClose} data-testid="dv3-presign-improve-cancel">{T("取消")}</Button>
        <Button size="sm" className="bg-orange-600 hover:bg-orange-700"
          disabled={busy || !due || !requirement.trim()} onClick={submit}
          data-testid="dv3-presign-improve-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />}
          {T("提交改进要求")}
        </Button>
      </>}>
      <p className="rounded-md bg-orange-50 px-2.5 py-2 text-[11px] leading-5 text-orange-800">
        {T("提交后工单状态变为「待改进」，设计师会收到提醒；列表的「要求完成时间」将替换为你填写的下一次时间，上一版方案与旧时间都会保留在详情记录里。")}
      </p>

      <label className="block text-[11px] font-semibold text-slate-600">
        {T("改进需求")} <span className="text-red-500">*</span>
        <textarea value={requirement} onChange={(e) => setRequirement(e.target.value)} rows={4} disabled={busy}
          data-testid="dv3-presign-improve-reason"
          placeholder={T("例如：客户希望后院增加露台，效果图需重做；主卧配色改为暖色")}
          className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
        <span className="mt-1 block text-[10px] font-normal text-slate-400">
          {T("写清客户的具体意见，设计师按这条内容修改。")}
        </span>
      </label>

      <label className="block text-[11px] font-semibold text-slate-600">
        {T("下一次要求完成时间（洛杉矶时间）")} <span className="text-red-500">*</span>
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} disabled={busy}
          data-testid="dv3-presign-improve-due"
          className="mt-1 w-full min-h-11 rounded-lg border border-slate-200 px-2 py-1.5 text-xs md:min-h-0" />
        <span className="mt-1 block text-[10px] font-normal text-slate-400">
          {row.dueAt ? T("原要求完成时间 {0}，可在此顺延或重设。", [fmtCN(row.dueAt, false)]) : T("原工单没有要求完成时间，请填写新的截止日期。")}
        </span>
        <UsDateHint compact testid="dv3-presign-improve-due-tz" className="mt-1" />
      </label>

      {/* 佐证附件（可选）：客户微信截图 / 现场照片 / 竞品报价等。
          会随这一轮改进要求一起进详情「改进要求记录」，设计师改稿时直接能看到依据。 */}
      <div>
        <p className="mb-1 text-[11px] font-semibold text-slate-600">{T("佐证附件（可选）")}</p>
        <input ref={inputRef} type="file" multiple accept={DESIGN_UPLOAD_ACCEPT} className="hidden"
          onChange={add} data-testid="dv3-presign-improve-file-input" />
        <Button size="sm" variant="outline" className="w-full" disabled={busy}
          onClick={() => inputRef.current?.click()} data-testid="dv3-presign-improve-pick">
          <Paperclip className="h-4 w-4" />{T("选择文件（可多选）")}
        </Button>
        <p className="mt-1 text-[10px] text-slate-400">
          {T("客户截图、现场照片、竞品报价等都可以；不传也能提交改进要求。")}
        </p>
        {files.length > 0 && (
          <ul className="mt-2 space-y-1" data-testid="dv3-presign-improve-files">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center gap-1.5 rounded-md bg-slate-50 px-2 py-1 text-[11px] text-slate-600">
                <FileText className="h-3 w-3 shrink-0 text-slate-400" />
                <span className="min-w-0 flex-1 truncate" title={f.name}>{f.name}</span>
                <span className="shrink-0 tabular-nums text-slate-400">{(f.size / 1024 / 1024).toFixed(2)} MB</span>
                <button type="button" aria-label={T("移除 {0}", [f.name])} disabled={busy}
                  onClick={() => drop(i)} data-testid={`dv3-presign-improve-remove-${i}`}
                  className="p-0.5 text-slate-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </RowDialogShell>
  );
};

/* 列表行内操作 · 关闭工单（2026-10-22 用户需求）
   需求原话
   --------
   「签约前营销的 12835 项目现在客户不需要了，设计师不想现在再列表需要单独给个状态」
   「做成已关闭……并且记得销售端要对应的显示并且能够恢复哦」
   「设计师可以主动点击关闭，点击是确认，并让写关闭理由或上传附件（附件可选）」

   设计取舍
   --------
   · **理由必填、附件可选**：客户为什么不做只有人知道，没有理由的关闭事后无法复盘；
     附件（客户微信截图 / 竞品报价 / 现场照片）是佐证，但不是每次都有，所以选填。
     后端 `/close` 同样校验理由，前端禁用按钮只是第一道闸。
   · 附件走**同一个请求**提交（multipart），不进 draftFiles：
     草稿区是「本轮待提交方案」的语义，把关闭佐证混进去会让设计师以为
     这些文件是要交付的方案；分成两步还会在用户传完附件却没点关闭时留下孤儿文件。
   · 关闭后工单从「待制作」页签、设计师工作台、节点逾期与停滞催办里全部移出，
     但版本记录与附件完整保留 —— 关闭不是删除。
   · 弹窗里明确写出「可以恢复」，避免用户以为关闭是永久删除而不敢点。 */
const CloseOrderDialog = ({ api, row, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const add = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) setFiles((old) => [...old, ...picked]);
    if (inputRef.current) inputRef.current.value = "";
  };
  const drop = (i) => setFiles((old) => old.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!reason.trim()) { toast.error(T("请填写关闭理由")); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("reason", reason.trim());
      files.forEach((f) => fd.append("files", f));
      await api.post(`/design/presign/orders/${row.leadId}/close`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(T("工单已关闭，已从待办与催办中移出；客户回心转意时可在列表中恢复"));
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
    setBusy(false);
  };

  return (
    <RowDialogShell tid="dv3-presign-close-dialog" title={T("关闭工单")}
      subtitle={`${row.address || ""}${row.projectId ? ` · ${row.projectId}` : ""}`}
      icon={Ban} iconCls="text-slate-600" onClose={onClose} busy={busy}
      footer={<>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onClose} data-testid="dv3-presign-close-cancel">{T("取消")}</Button>
        <Button size="sm" className="bg-slate-700 hover:bg-slate-800"
          disabled={busy || !reason.trim()} onClick={submit}
          data-testid="dv3-presign-close-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
          {T("确认关闭")}
        </Button>
      </>}>
      <p className="rounded-md bg-slate-50 px-2.5 py-2 text-[11px] leading-5 text-slate-700">
        {T("关闭表示客户不需要这个方案了，工单会从待制作、设计师工作台与逾期催办中移出。方案版本与附件全部保留；客户回心转意时可在列表点「恢复」回到关闭前的状态。")}
      </p>

      <label className="block text-[11px] font-semibold text-slate-600">
        {T("关闭理由")} <span className="text-red-500">*</span>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} disabled={busy}
          data-testid="dv3-presign-close-reason"
          placeholder={T("例如：客户已确定不做 ADU；客户选择了其他公司；客户资金问题暂停")}
          className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
        <span className="mt-1 block text-[10px] font-normal text-slate-400">
          {T("写清客户为什么不做了，方便日后复盘。")}
        </span>
      </label>

      <div>
        <p className="mb-1 text-[11px] font-semibold text-slate-600">
          {T("佐证附件（可选）")}
        </p>
        <input ref={inputRef} type="file" multiple accept={DESIGN_UPLOAD_ACCEPT} className="hidden"
          onChange={add} data-testid="dv3-presign-close-file-input" />
        <Button size="sm" variant="outline" className="w-full" disabled={busy}
          onClick={() => inputRef.current?.click()} data-testid="dv3-presign-close-pick">
          <Paperclip className="h-4 w-4" />{T("选择文件（可多选）")}
        </Button>
        <p className="mt-1 text-[10px] text-slate-400">
          {T("客户截图、竞品报价、现场照片等都可以；不传也能关闭。")}
        </p>
        {files.length > 0 && (
          <ul className="mt-2 space-y-1" data-testid="dv3-presign-close-files">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center gap-1.5 rounded-md bg-slate-50 px-2 py-1 text-[11px] text-slate-600">
                <FileText className="h-3 w-3 shrink-0 text-slate-400" />
                <span className="min-w-0 flex-1 truncate" title={f.name}>{f.name}</span>
                <span className="shrink-0 tabular-nums text-slate-400">{(f.size / 1024 / 1024).toFixed(2)} MB</span>
                <button type="button" aria-label={T("移除 {0}", [f.name])} disabled={busy}
                  onClick={() => drop(i)} data-testid={`dv3-presign-close-remove-${i}`}
                  className="p-0.5 text-slate-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </RowDialogShell>
  );
};
